import React, { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import DOMPurify from "dompurify";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { FileUploadButton } from "@components/FileUploadButton/FileUploadButton";
import { AddDocumentIcon, DocumentIcon, PencilIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormRadio } from "@components/inputs/form/FormRadio/FormRadio";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useCustomerStorageStore } from "@store/customerStorage/customerStorage";
import { type IUseModalProps } from "@store/modal/modal";
import { genericCustomerStorageRequest } from "@services/customerStorage/services";
import type {
  ICustomerStorageItemAction,
  IFileCreateData,
} from "@services/customerStorage/types";
import { isNullOrUndefined } from "@utils/common";
import { formatDate } from "@utils/units";

interface IFileModalForm {
  folderId?: number | null;
  file?: File;
  fileName: string;
  addExpirationDate: boolean;
  expirationDate?: Date | null;
  addReminder: boolean;
  reminderBefore: string | null;
  fileReminderTypeId: string | null;
}

type TFileMutationData = Omit<IFileModalForm, "expirationDate"> & {
  expirationDate: string | null;
};

export type TFileModalVariant = "edit" | "create" | "move";

interface IFileModalProps extends IUseModalProps {
  action: ICustomerStorageItemAction;
  fileCreationDataAction: ICustomerStorageItemAction;
  variant: TFileModalVariant;
  onSuccess?: () => void;
}

export const FileModal: React.FC<IFileModalProps> = ({
  action,
  fileCreationDataAction,
  variant,
  onSuccess,
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useModal(rest);
  const { currentFolderUrl } = useCustomerStorageStore();
  const queryClient = useQueryClient();

  const { data: fileCreationData } = useQuery({
    queryKey: ["customerStorage", "fileCreation"],
    queryFn: () =>
      genericCustomerStorageRequest<IFileCreateData>({
        requestProps: fileCreationDataAction,
      }),
  });

  const { mutateAsync: mutateFile, isPending } = useMutation({
    mutationFn: (data: TFileMutationData) =>
      genericCustomerStorageRequest({
        requestProps: action,
        data,
        ...(variant === "create"
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["customerStorage", currentFolderUrl],
      });
      void queryClient.invalidateQueries({
        queryKey: ["customerStorage", "search"],
      });

      toast.success(t(`customerStorage.actions.file.${variant}.toast.success`));
      modal.close();
      onSuccess?.();
    },
  });

  const defaultValues: IFileModalForm = useMemo(() => {
    return {
      folderId: null,
      fileName: "",
      addExpirationDate: false,
      expirationDate: null,
      addReminder: false,
      reminderBefore: null,
      fileReminderTypeId: "",
    };
  }, []);

  const {
    availableFileReminders,
    availableFolders,
    availableFileReminderTypeIds,
    fileDataFormValues,
  } = useMemo(() => {
    if (!fileCreationData) {
      return {
        fileDataFormValues: defaultValues,
      };
    }

    const { fileReminderBefores, folders, fileReminderTypeIds, ...fileData } =
      fileCreationData;

    const availableFileReminders =
      fileReminderBefores?.map((reminder) => {
        return {
          value: reminder,
          label: t(`customerStorage.actions.file.reminder.${reminder}`),
        };
      }) ?? [];

    const availableFolders = folders
      ? [
          ...folders.map(({ folderId, name, isDefault, shouldTranslate }) => {
            const folderName =
              (!isNullOrUndefined(isDefault) && isDefault) ||
              (!isNullOrUndefined(shouldTranslate) && shouldTranslate)
                ? t(`customerStorage.actions.file.${name}`)
                : name;

            return {
              value: folderId ?? 0,
              label: folderName,
            };
          }),
        ]
      : [];

    const availableFileReminderTypeIds =
      fileReminderTypeIds?.map((reminderTypeId) => {
        return {
          value: reminderTypeId,
          label: t(
            `customerStorage.form.fields.file.radio.${reminderTypeId}.label`,
          ),
        };
      }) ?? [];

    const fileDataFormValues: IFileModalForm = {
      ...defaultValues,
      ...fileData,
      folderId:
        variant === "edit"
          ? (fileData.folderId ?? 0)
          : variant === "move"
            ? null
            : (availableFolders[0]?.value ?? 0),
      reminderBefore:
        fileData.reminderBefore ?? availableFileReminders[0]?.value ?? null,
      fileReminderTypeId:
        fileData.fileReminderTypeId ??
        availableFileReminderTypeIds[0]?.value ??
        null,
    };

    return {
      availableFileReminders,
      availableFolders,
      availableFileReminderTypeIds,
      fileDataFormValues,
    };
  }, [fileCreationData, defaultValues, variant, t]);

  const { handleSubmit, control, setValue, setError } = useForm<IFileModalForm>(
    {
      values: fileDataFormValues,
    },
  );
  const [
    addExpirationDateValue,
    addReminderValue,
    fileReminderTypeId,
    file,
    folderIdValue,
  ] = useWatch({
    name: [
      "addExpirationDate",
      "addReminder",
      "fileReminderTypeId",
      "file",
      "folderId",
    ],
    control,
  });

  const { setApiErrors, getApiErrors } = useApiErrorUtils(setError);

  const submitFile: SubmitHandler<IFileModalForm> = async (data) => {
    const { file, folderId, expirationDate, ...rest } = data;

    if (!file && variant === "create") {
      toast.error("customerStorage.toast.fileNotSelected");
      return;
    }

    await mutateFile({
      ...rest,
      file,
      folderId: folderId !== 0 ? folderId : null,
      fileName:
        variant === "edit" && data.fileName
          ? data.fileName
          : (file?.name ?? ""),
      expirationDate: expirationDate ? formatDate(expirationDate) : null,
    }).catch((error: AxiosError) => {
      const errors = getApiErrors(error, false);

      //assume requested was cancelled
      if (!errors.length) {
        toast.error("customerStorage.toast.fileUploadFailed");
        return;
      }

      setApiErrors(error, false);
    });
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: variant === "edit" ? PencilIcon : AddDocumentIcon,
        color: "green",
      }}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={modal.close}
            type="button"
            className="w-full"
          >
            {t(`customerStorage.actions.file.${variant}.buttons.cancel`)}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit(submitFile)}
            className="w-full"
            isLoading={isPending}
            disabled={
              (variant === "create" && !file) ||
              (variant === "move" && isNullOrUndefined(folderIdValue))
            }
          >
            {t(`customerStorage.actions.file.${variant}.buttons.submit`)}
          </Button>
        </>
      }
      title={t(`customerStorage.actions.file.${variant}.title`)}
      className="md:max-w-xl"
    >
      <form
        onSubmit={handleSubmit(submitFile)}
        className="flex w-full flex-col gap-5"
      >
        {availableFolders && (
          <FormSelect
            name="folderId"
            rules={{ required: variant === "create" }}
            control={control}
            options={availableFolders}
            label={t("customerStorage.form.fields.file.folderId.label")}
          />
        )}
        {variant === "create" && (
          <>
            <FileUploadButton
              name="file"
              control={control}
              setValue={setValue}
              disabled={isPending}
            />
            {file?.name && (
              <Notification
                color="green"
                icon={DocumentIcon}
                description={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        t("fileUpload.chosenFile", {
                          fileName: file.name ?? "",
                        }),
                      ),
                    }}
                  />
                }
              />
            )}
          </>
        )}
        {variant === "edit" && (
          <FormInput
            name="fileName"
            rules={{ required: variant === "edit" }}
            control={control}
            label={t("customerStorage.form.fields.file.fileName.label")}
          />
        )}
        {variant !== "move" && (
          <>
            <FormCheckbox
              name="addExpirationDate"
              control={control}
              label={t(
                "customerStorage.form.fields.file.addExpirationDate.label",
              )}
              labelClassName="text-grey-dark"
            />
            {addExpirationDateValue && (
              <FormDatepicker
                name="expirationDate"
                control={control}
                label={t(
                  "customerStorage.form.fields.file.expirationDate.label",
                )}
              />
            )}
            {addExpirationDateValue && (
              <>
                <FormCheckbox
                  name="addReminder"
                  control={control}
                  label={t(
                    "customerStorage.form.fields.file.addReminder.label",
                  )}
                  labelClassName="text-grey-dark"
                />
                {addReminderValue && availableFileReminders && (
                  <FormSelect
                    name="reminderBefore"
                    control={control}
                    label={t(
                      "customerStorage.form.fields.file.reminderBefore.label",
                    )}
                    options={availableFileReminders}
                  />
                )}
                {addReminderValue &&
                  availableFileReminderTypeIds &&
                  fileReminderTypeId && (
                    <FormRadio
                      name="fileReminderTypeId"
                      control={control}
                      defaultValue={fileReminderTypeId}
                      options={availableFileReminderTypeIds}
                    />
                  )}
              </>
            )}
          </>
        )}
      </form>
    </Modal>
  );
};
